import * as React from "react";
import {
  Popover as ShadcnPopover,
  PopoverTrigger as ShadcnPopoverTrigger,
  PopoverContent as ShadcnPopoverContent,
} from "../popover";
import { Button } from "./button-adapter";
import { cn } from "@/lib/utils";

// Context to share close function and offset
interface PopoverContextValue {
  close: (() => void) | null;
  offset?: number;
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end"
    | "left-start"
    | "left-end"
    | "right-start"
    | "right-end";
  showArrow?: boolean;
  classNames?: {
    base?: string;
    content?: string;
    trigger?: string;
  };
}

const PopoverContext = React.createContext<PopoverContextValue>({
  close: null,
});

export interface PopoverProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  placement?:
    | "top"
    | "bottom"
    | "left"
    | "right"
    | "top-start"
    | "top-end"
    | "bottom-start"
    | "bottom-end"
    | "left-start"
    | "left-end"
    | "right-start"
    | "right-end";
  showArrow?: boolean;
  offset?: number;
  children:
    | React.ReactNode
    | ((props: { close: () => void }) => React.ReactNode);
  as?: React.ElementType;
  className?: string;
  style?: React.CSSProperties;
  classNames?: {
    base?: string;
    content?: string;
    trigger?: string;
  };
}

const Popover: React.FC<PopoverProps> = ({
  isOpen,
  onOpenChange,
  placement,
  showArrow,
  offset,
  children,
  as,
  className,
  style,
  classNames,
}) => {
  const close = React.useCallback(() => {
    onOpenChange?.(false);
  }, [onOpenChange]);

  // If 'as' prop is provided, render as that element (for compatibility with Headless UI)
  if (as) {
    const Component = as;
    return (
      <Component className={cn(classNames?.base, className)} style={style}>
        {children}
      </Component>
    );
  }

  // Otherwise, render as Radix Popover
  const needsWrapper = (className ?? style ?? classNames?.base) != null;
  const childrenContent =
    typeof children === "function" ? children({ close }) : children;

  return (
    <PopoverContext.Provider
      value={{ close, offset, placement, showArrow, classNames }}
    >
      <ShadcnPopover open={isOpen} onOpenChange={onOpenChange}>
        {needsWrapper ? (
          <div className={cn(classNames?.base, className)} style={style}>
            {childrenContent}
          </div>
        ) : (
          childrenContent
        )}
      </ShadcnPopover>
    </PopoverContext.Provider>
  );
};

export interface PopoverTriggerProps extends React.ComponentPropsWithoutRef<
  typeof ShadcnPopoverTrigger
> {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

const PopoverTrigger: React.FC<PopoverTriggerProps> = ({
  children,
  asChild,
  className,
  ...props
}) => {
  const context = React.useContext(PopoverContext);
  // Auto-detect if child is a Button or button element and use asChild to avoid button nesting
  let shouldUseAsChild = asChild;

  if (shouldUseAsChild === undefined) {
    if (React.isValidElement(children)) {
      const childType = children.type;

      if (typeof childType === "string" && childType === "button") {
        shouldUseAsChild = true;
      } else if (typeof childType === "function") {
        const typeAny = childType as any;
        if (typeAny?.displayName === "Button") {
          shouldUseAsChild = true;
        } else if (typeAny?.name === "Button") {
          shouldUseAsChild = true;
        } else if (typeAny?.render) {
          const render = typeAny.render;
          if (render?.displayName === "Button" || render?.name === "Button") {
            shouldUseAsChild = true;
          }
        }
      }
    }
  }

  return (
    <ShadcnPopoverTrigger
      asChild={shouldUseAsChild ?? false}
      className={cn(context.classNames?.trigger, className)}
      {...props}
    >
      {children}
    </ShadcnPopoverTrigger>
  );
};

export interface PopoverContentProps {
  children: React.ReactNode;
  className?: string;
  placement?: "top" | "bottom" | "left" | "right";
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  offset?: number;
}

const PopoverContent: React.FC<PopoverContentProps> = ({
  children,
  className,
  placement = "bottom",
  side,
  align = "center",
  offset,
}) => {
  const context = React.useContext(PopoverContext);

  // Parse compound placements like "top-end" -> side="top", align="end"
  const parsePlacement = (
    placement?: string
  ): {
    side: "top" | "bottom" | "left" | "right";
    align: "start" | "center" | "end";
  } => {
    if (!placement) return { side: "bottom", align: "center" };

    if (placement.includes("-")) {
      const [sidePart, alignPart] = placement.split("-");
      return {
        side: sidePart as "top" | "bottom" | "left" | "right",
        align:
          alignPart === "start"
            ? "start"
            : alignPart === "end"
              ? "end"
              : "center",
      };
    }

    const sideMap = {
      top: "top",
      bottom: "bottom",
      left: "left",
      right: "right",
    } as const;

    return {
      side: sideMap[placement as keyof typeof sideMap] ?? "bottom",
      align: "center",
    };
  };

  let finalSide: "top" | "bottom" | "left" | "right";
  let finalAlign: "start" | "center" | "end";

  if (side !== undefined) {
    finalSide = side;
    finalAlign = align ?? "center";
  } else {
    const contextPlacement = context.placement ?? placement;
    const parsed = parsePlacement(contextPlacement);
    finalSide = parsed.side;
    finalAlign = parsed.align;
  }

  const finalOffset = offset ?? context.offset ?? 4;

  return (
    <ShadcnPopoverContent
      side={finalSide}
      align={finalAlign}
      sideOffset={finalOffset}
      className={cn("relative", context.classNames?.content, className)}
    >
      {context.showArrow && (
        <div
          className={cn(
            "absolute w-2 h-2 bg-white border-l border-t border-secondary-light rotate-45 z-10",
            finalSide === "top" && "-bottom-1 left-1/2 -translate-x-1/2",
            finalSide === "bottom" && "-top-1 left-1/2 -translate-x-1/2",
            finalSide === "left" && "-right-1 top-1/2 -translate-y-1/2",
            finalSide === "right" && "-left-1 top-1/2 -translate-y-1/2"
          )}
        />
      )}
      {children}
    </ShadcnPopoverContent>
  );
};

// Headless UI compatibility exports
export interface PopoverButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  className?: string;
}

const PopoverButton: React.FC<PopoverButtonProps> = ({
  children,
  className,
  ...props
}) => {
  // Auto-detect if child is a Button or button element and use asChild to avoid button nesting
  let shouldUseAsChild = false;

  if (React.isValidElement(children)) {
    const childType = children.type;

    // Direct comparison with Button component
    if (childType === Button) {
      shouldUseAsChild = true;
    }
    // Check for native button element
    else if (typeof childType === "string" && childType === "button") {
      shouldUseAsChild = true;
    }
    // Check displayName (works for forwardRef components)
    else if (typeof childType === "function") {
      const typeAny = childType as any;
      if (typeAny?.displayName === "Button") {
        shouldUseAsChild = true;
      }
      // Check name as fallback
      else if (typeAny?.name === "Button") {
        shouldUseAsChild = true;
      }
      // For forwardRef, check the render function
      else if (typeAny?.render) {
        const render = typeAny.render;
        if (render?.displayName === "Button" || render?.name === "Button") {
          shouldUseAsChild = true;
        }
      }
    }
  }

  return (
    <PopoverTrigger
      asChild={shouldUseAsChild}
      className={cn(!shouldUseAsChild && "border-secondary-light", className)}
      {...props}
    >
      {children}
    </PopoverTrigger>
  );
};

export interface PopoverOverlayProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "translate"
> {
  translate?: "yes" | "no";
  className?: string;
}

const PopoverOverlay: React.FC<PopoverOverlayProps> = ({
  translate,
  className,
  ...props
}) => {
  // Radix Popover doesn't have a separate overlay, it's part of the content
  // This is a no-op for compatibility
  return null;
};

export interface PopoverPanelProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "translate" | "children"
> {
  children:
    | React.ReactNode
    | ((props: { close: () => void }) => React.ReactNode);
  className?: string;
  anchor?: {
    to?: string;
    gap?: string;
  };
  translate?: "yes" | "no";
}

const PopoverPanel: React.FC<PopoverPanelProps> = ({
  children,
  className,
  anchor,
  translate,
  ...props
}) => {
  // Get close function and other values from context
  const context = React.useContext(PopoverContext);
  const close = context.close ?? (() => {});

  const content =
    typeof children === "function" ? children({ close }) : children;

  // Parse anchor positioning or use context placement
  const parsePlacement = (
    placement?: string
  ): {
    side: "top" | "bottom" | "left" | "right";
    align: "start" | "center" | "end";
  } => {
    if (!placement) return { side: "bottom", align: "center" };

    if (placement.includes("-")) {
      const [sidePart, alignPart] = placement.split("-");
      return {
        side: sidePart as "top" | "bottom" | "left" | "right",
        align:
          alignPart === "start"
            ? "start"
            : alignPart === "end"
              ? "end"
              : "center",
      };
    }

    const sideMap = {
      top: "top",
      bottom: "bottom",
      left: "left",
      right: "right",
    } as const;

    return {
      side: sideMap[placement as keyof typeof sideMap] ?? "bottom",
      align: "center",
    };
  };

  let side: "top" | "bottom" | "left" | "right";
  let align: "start" | "center" | "end";

  if (anchor?.to) {
    side = anchor.to.includes("top")
      ? "top"
      : anchor.to.includes("bottom")
        ? "bottom"
        : anchor.to.includes("left")
          ? "left"
          : anchor.to.includes("right")
            ? "right"
            : "bottom";
    align = anchor.to.includes("end")
      ? "end"
      : anchor.to.includes("start")
        ? "start"
        : "center";
  } else {
    const parsed = parsePlacement(context.placement);
    side = parsed.side;
    align = parsed.align;
  }

  return (
    <PopoverContent
      className={cn(className)}
      side={side}
      align={align}
      offset={context.offset}
      {...props}
    >
      {content}
    </PopoverContent>
  );
};

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverButton,
  PopoverPanel,
  PopoverOverlay,
};
